
import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme, useColor } from '../../context/ColorContext';
import { turkishSearch } from '../../db/db';

const SEARCH_TERMS = [
    "Dış Cephe Boyaları",
    "İç Cephe Renkleri",
    "İpek Mat Boyalar",
    "Silikonlu Boyalar",
    "2024 Renk Trendleri",
    "Su Bazlı Boyalar"
];

const SearchModal = ({ isOpen, onClose }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [placeholder, setPlaceholder] = useState("");
    const [termIndex, setTermIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const { theme } = useTheme();
    const { brandData } = useColor();
    const navigate = useNavigate();

    const isDark = theme === 'dark';

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsAnimatingOut(false);
        } else if (shouldRender) {
            setIsAnimatingOut(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsAnimatingOut(false);
                setSearchTerm("");
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, shouldRender]);

    useEffect(() => {
        if (!isOpen) return;

        const currentTerm = SEARCH_TERMS[termIndex];
        const typingSpeed = isDeleting ? 40 : 100;

        const timeout = setTimeout(() => {
            if (!isDeleting && charIndex < currentTerm.length) {
                setPlaceholder(currentTerm.substring(0, charIndex + 1));
                setCharIndex(prev => prev + 1);
            } else if (isDeleting && charIndex > 0) {
                setPlaceholder(currentTerm.substring(0, charIndex - 1));
                setCharIndex(prev => prev - 1);
            } else if (!isDeleting && charIndex === currentTerm.length) {
                setTimeout(() => setIsDeleting(true), 1500);
            } else if (isDeleting && charIndex === 0) {
                setIsDeleting(false);
                setTermIndex((prev) => (prev + 1) % SEARCH_TERMS.length);
            }
        }, typingSpeed);

        return () => clearTimeout(timeout);
    }, [isOpen, termIndex, charIndex, isDeleting]);

    const results = useMemo(() => {
        const q = searchTerm.trim();
        if (!q) return [];
        const products = brandData?.PRODUCTS || [];
        return products
            .filter(p => {
                const haystack = [
                    p.name,
                    p.description,
                    p.productCode,
                    ...(p.categoryNames || [])
                ].filter(Boolean).join(' ');
                return turkishSearch(haystack, q);
            })
            .slice(0, 30);
    }, [searchTerm, brandData?.PRODUCTS]);

    const handleSelect = (product) => {
        navigate(`/urunler/${product.slug || product.id}`);
        onClose();
    };

    if (!shouldRender) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-20 md:pt-32">
            {/* Backdrop */}
            <div
                className={`absolute inset-0 backdrop-blur-3xl transition-colors duration-500 ${isDark ? 'bg-black/60' : 'bg-white/40'
                    } ${isAnimatingOut ? 'animate-fadeOut' : 'animate-fadeIn'}`}
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className={`relative w-full max-w-4xl px-4 ${isAnimatingOut ? 'animate-zoomOut' : 'animate-zoomIn'}`}>
                <button
                    onClick={onClose}
                    className={`absolute -top-16 right-4 p-3 rounded-full transition-all hover:rotate-90 shadow-xl ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-slate-900'
                        }`}
                >
                    <X size={32} />
                </button>

                <div className={`relative rounded-3xl p-6 md:p-10 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] border ${isDark ? 'bg-black/40 border-white/10' : 'bg-white/80 border-black/5'
                    } backdrop-blur-2xl`}>
                    <div className="relative">
                        <Search className={`absolute left-0 top-1/2 -translate-y-1/2 w-7 h-7 md:w-10 md:h-10 ${isDark ? 'text-[#5AA668]' : 'text-[#4a8c56]'
                            }`} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                            placeholder={placeholder}
                            className={`w-full bg-transparent border-b-2 py-5 pl-10 md:pl-16 pr-4 text-xl md:text-4xl font-black focus:outline-none transition-all ${isDark
                                    ? 'text-white border-white/20 focus:border-[#5AA668] placeholder-white/20'
                                    : 'text-slate-900 border-black/10 focus:border-[#5AA668] placeholder-slate-400'
                                }`}
                        />
                    </div>

                    {/* Results */}
                    {searchTerm.trim() && (
                        <div className="mt-6">
                            <span className={`text-[10px] md:text-xs font-bold uppercase tracking-widest block mb-3 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                                {results.length > 0 ? `${results.length} Ürün Bulundu` : 'Sonuç Yok'}
                            </span>
                            {results.length > 0 ? (
                                <div className={`max-h-[60vh] overflow-y-auto rounded-2xl divide-y ${isDark ? 'divide-white/5' : 'divide-black/5'}`}>
                                    {results.map(product => (
                                        <button
                                            key={product.id}
                                            type="button"
                                            onClick={() => handleSelect(product)}
                                            className={`w-full text-left px-3 py-3 flex items-center gap-4 transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}
                                        >
                                            <div className={`shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-black/5'}`}>
                                                {product.image ? (
                                                    <img
                                                        src={product.image}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <Package size={20} className={isDark ? 'text-white/30' : 'text-slate-400'} />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className={`text-sm md:text-base font-black uppercase tracking-tight truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                                    {product.name}
                                                </div>
                                                {(product.categoryNames?.[0] || product.productCode) && (
                                                    <div className={`text-[10px] md:text-xs font-bold uppercase tracking-wider truncate ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                                                        {[product.categoryNames?.[0], product.productCode].filter(Boolean).join(' · ')}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className={`py-8 text-center text-sm ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                                    "{searchTerm}" için eşleşen ürün bulunamadı.
                                </p>
                            )}
                        </div>
                    )}

                    {!searchTerm.trim() && (
                        <div className="mt-6 flex flex-wrap gap-2 md:gap-3">
                            <span className={`text-[10px] md:text-xs font-bold uppercase tracking-widest w-full mb-2 ${isDark ? 'text-white/40' : 'text-slate-500'
                                }`}>Popüler Aramalar</span>
                            {SEARCH_TERMS.slice(0, 5).map((term) => (
                                <button
                                    key={term}
                                    onClick={() => setSearchTerm(term)}
                                    className={`px-4 py-2 rounded-full text-xs md:text-sm font-bold transition-all border ${isDark
                                            ? 'bg-white/5 border-white/10 text-white/70 hover:bg-[#5AA668] hover:text-white hover:border-transparent'
                                            : 'bg-black/5 border-black/5 text-slate-600 hover:bg-[#5AA668] hover:text-white hover:border-transparent'
                                        }`}
                                >
                                    {term}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchModal;
